import { useState, useEffect } from "react"
import { useNavigate } from "react-router"
import { useAuthStore } from "@/shared/hooks/use-auth-store"
import { useToast } from "@/client/components/common/toast"
import { getBrowserSupabaseClient } from "@/lib/supabase/client"
import styles from "./me-page.module.css"

interface UserProfile {
  level: number
  points: number
  completed_tasks: number
  active_tasks: number
  total_earnings: number
  balance: number
}

const VERIFY_TYPE_LABEL: Record<string, string> = {
  enterprise: "企业认证",
  engineer: "个人开发者认证",
  student: "学生认证",
}

interface StatCardProps {
  keyName: keyof UserProfile
  label: string
  value: number | string
  gradient: string
  icon: "check" | "time" | "star" | "money"
  prefix?: string
}

interface MenuItemProps {
  icon: "resume" | "verify" | "points" | "wallet" | "settings"
  label: string
  onClick: () => void
  index: number
}

const icons = {
  check: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
  time: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <polyline points="12 6 12 12 16 14"/>
    </svg>
  ),
  star: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
    </svg>
  ),
  money: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23"/>
      <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
    </svg>
  ),
  resume: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/>
      <line x1="16" y1="17" x2="8" y2="17"/>
      <polyline points="10 9 9 9 8 9"/>
    </svg>
  ),
  verify: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/>
      <polyline points="22 4 12 14.01 9 11.01"/>
    </svg>
  ),
  points: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <path d="M12 6v6l4 2"/>
    </svg>
  ),
  wallet: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 4H3a2 2 0 00-2 2v12a2 2 0 002 2h18a2 2 0 002-2V6a2 2 0 00-2-2z"/>
      <circle cx="16" cy="12" r="1.5" fill="currentColor"/>
    </svg>
  ),
  settings: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z"/>
    </svg>
  ),
}

function StatCard({ label, value, gradient, icon, prefix }: Omit<StatCardProps, "keyName">) {
  return (
    <div className={styles.statCard} style={{ "--stat-gradient": gradient } as React.CSSProperties}>
      <div className={styles.statIconWrapper}>
        <div className={styles.statIcon}>{icons[icon]}</div>
      </div>
      <div className={styles.statContent}>
        <span className={styles.statValue}>{prefix}{value}</span>
        <span className={styles.statLabel}>{label}</span>
      </div>
    </div>
  )
}

function MenuItem({ icon, label, onClick, index }: MenuItemProps) {
  return (
    <button
      className={styles.menuItem}
      onClick={onClick}
      style={{ "--item-index": index } as React.CSSProperties}
    >
      <div className={styles.menuIconWrapper}>
        <div className={styles.menuIcon}>{icons[icon]}</div>
      </div>
      <span className={styles.menuLabel}>{label}</span>
      <div className={styles.menuArrow}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6"/>
        </svg>
      </div>
    </button>
  )
}

function AchievementItem({ icon, label, unlocked }: { icon: string; label: string; unlocked?: boolean }) {
  return (
    <div className={`${styles.achievementItem} ${unlocked ? styles.achievementUnlocked : ""}`}>
      <div className={styles.achievementIcon}>{icon}</div>
      <span className={styles.achievementName}>{label}</span>
    </div>
  )
}

export default function MePage() {
  const { user, logout } = useAuthStore()
  const toast = useToast()
  const navigate = useNavigate()
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(false)
  const [verifiedType, setVerifiedType] = useState<string | null>(null)

  const nickname = user?.user_metadata?.nickname || user?.email?.split("@")[0] || "用户"
  const avatarLetter = nickname[0]?.toUpperCase() || "U"
  const isEmailVerified = !!user?.email_confirmed_at

  useEffect(() => {
    const sb = getBrowserSupabaseClient()
    const uid = user?.id
    if (!sb || !uid) return

    const fetchProfile = async () => {
      const [
        userResult,
        completedResult,
        activeResult,
        balanceResult,
        earningsResult,
      ] = await Promise.all([
        sb.from("users").select("level, points").eq("id", uid).single(),
        sb.from("bounties").select("*", { count: "exact", head: true }).eq("claimed_by", uid).eq("status", "completed"),
        sb.from("bounties").select("*", { count: "exact", head: true }).eq("claimed_by", uid).in("status", ["in_progress", "reviewing"]),
        sb.from("point_balances").select("balance").eq("user_id", uid).single(),
        sb.from("bounties").select("reward_usdc").eq("claimed_by", uid).eq("status", "completed"),
      ])

      const totalEarnings = (earningsResult.data || []).reduce((sum: number, b: { reward_usdc: number | null }) => sum + (b.reward_usdc || 0), 0)

      setProfile({
        level: userResult.data?.level ?? 1,
        points: userResult.data?.points ?? 0,
        completed_tasks: completedResult.count ?? 0,
        active_tasks: activeResult.count ?? 0,
        total_earnings: totalEarnings,
        balance: balanceResult.data?.balance ?? 0,
      })

      // 查询已通过的认证类型
      const { data: verifyData } = await sb
        .from("verify_applications")
        .select("verify_type")
        .eq("user_id", uid)
        .eq("status", "approved")
        .order("reviewed_at", { ascending: false })
        .limit(1)
        .maybeSingle()

      if (verifyData) {
        setVerifiedType((verifyData as { verify_type: string }).verify_type)
      }
    }

    fetchProfile()

    const channel = sb
      .channel("me-balance")
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "point_balances", filter: `user_id=eq.${uid}` },
        (payload) => {
          const newBalance = Number((payload.new as Record<string, unknown>).balance ?? 0)
          setProfile((prev) => prev ? { ...prev, balance: newBalance } : prev)
        },
      )
      .subscribe()

    return () => {
      sb.removeChannel(channel)
    }
  }, [user])

  async function handleLogout() {
    if (loading) return
    setLoading(true)
    await logout()
    toast.success("已退出登录")
    navigate("/login", { replace: true })
  }

  function handleMenuClick(label: string) {
    const routes: Record<string, string> = {
      "我的简历": "/me/resume",
      "认证中心": "/verify",
      "积分记录": "/points",
      "钱包": "/wallet",
      "设置": "/settings",
    }
    const route = routes[label]
    if (route) {
      navigate(route)
    } else {
      toast.info(`${label}功能即将上线`)
    }
  }

  const menuItems: Array<{ icon: MenuItemProps["icon"]; label: string }> = [
    { icon: "resume", label: "我的简历" },
    { icon: "verify", label: "认证中心" },
    { icon: "points", label: "积分记录" },
    { icon: "wallet", label: "钱包" },
    { icon: "settings", label: "设置" },
  ]

  const statCards: Array<Omit<StatCardProps, "keyName" | "value">> = [
    {
      label: "已完成",
      gradient: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
      icon: "check",
    },
    {
      label: "进行中",
      gradient: "linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)",
      icon: "time",
    },
    {
      label: "积分",
      gradient: "linear-gradient(135deg, #10B981 0%, #06B6D4 100%)",
      icon: "star",
    },
    {
      label: "收益",
      gradient: "linear-gradient(135deg, #EC4899 0%, #A855F7 100%)",
      icon: "money",
      prefix: "¥",
    },
  ]

  const achievements = [
    { icon: "🎯", label: "首次提交", unlocked: true },
    { icon: "🔥", label: "连续签到", unlocked: true },
    { icon: "⭐", label: "五星好评", unlocked: false },
    { icon: "🏆", label: "任务达人", unlocked: false },
  ]

  return (
    <div className={styles.page}>
      {/* 背景渐变层 */}
      <div className={styles.background}>
        <div className={styles.gradientOrb} />
        <div className={styles.gradientMesh} />
      </div>

      {/* 页面内容 */}
      <div className={styles.container}>
        {/* 顶部导航 */}
        <header className={styles.header}>
          <h1 className={styles.headerTitle}>个人中心</h1>
        </header>

        {/* 用户信息卡片 - 玻璃态效果 */}
        <div className={styles.profileCard}>
          <div className={styles.profileGlow} />
          <div className={styles.profileContent}>
            <div className={styles.avatarSection}>
              <div className={styles.avatarWrapper}>
                <div className={styles.avatar}>{avatarLetter}</div>
                <div className={styles.avatarRing} />
                {!isEmailVerified && <div className={styles.avatarBadge} title="邮箱未验证">!</div>}
              </div>
              <button
                className={styles.editBtn}
                onClick={() => navigate("/me/edit")}
                aria-label="编辑资料"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </button>
            </div>

            <div className={styles.infoSection}>
              <div className={styles.nameRow}>
                <h2 className={styles.nickname}>{nickname}</h2>
                {isEmailVerified && verifiedType ? (
                  <span className={styles.verifiedBadge}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                    </svg>
                    {VERIFY_TYPE_LABEL[verifiedType] ?? verifiedType}
                  </span>
                ) : isEmailVerified ? (
                  <span className={styles.verifiedBadge}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                    </svg>
                    已认证
                  </span>
                ) : (
                  <span className={styles.unverifiedBadge}>未认证</span>
                )}
              </div>
              <p className={styles.userEmail}>{user?.email}</p>
            </div>
          </div>
        </div>

        {/* 邮箱验证提示 */}
        {!isEmailVerified && (
          <div className={styles.emailAlert}>
            <div className={styles.emailAlertIcon}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
            </div>
            <div className={styles.emailAlertContent}>
              <p className={styles.emailAlertTitle}>验证邮箱解锁更多功能</p>
              <p className={styles.emailAlertDesc}>完成邮箱验证后即可提现收益、参与更多任务</p>
            </div>
            <button
              className={styles.emailAlertBtn}
              onClick={() => navigate("/verify")}
            >
              去验证
            </button>
          </div>
        )}

        {/* 统计数据卡片 */}
        <section className={styles.statsSection}>
          <div className={styles.statsGrid}>
            {statCards.map((card) => {
              const value = card.label === "收益"
                ? (profile?.total_earnings ?? 0).toFixed(0)
                : card.label === "积分"
                  ? (profile?.balance ?? 0)
                  : (profile?.[card.label === "已完成" ? "completed_tasks" : "active_tasks"] ?? 0)
              return (
                <StatCard
                  key={card.label}
                  label={card.label}
                  value={value}
                  gradient={card.gradient}
                  icon={card.icon}
                  prefix={card.prefix}
                />
              )
            })}
          </div>
        </section>

        {/* 功能菜单 */}
        <section className={styles.menuSection}>
          <h3 className={styles.sectionTitle}>常用功能</h3>
          <div className={styles.menuList}>
            {menuItems.map((item, index) => (
              <MenuItem
                key={item.label}
                icon={item.icon}
                label={item.label}
                onClick={() => handleMenuClick(item.label)}
                index={index}
              />
            ))}
          </div>
        </section>

        {/* 成就展示 */}
        <section className={styles.achievementsSection}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>实战成果</h3>
            <button className={styles.viewAllBtn}>查看全部</button>
          </div>
          <div className={styles.achievementsGrid}>
            {achievements.map((ach) => (
              <AchievementItem
                key={ach.label}
                icon={ach.icon}
                label={ach.label}
                unlocked={ach.unlocked}
              />
            ))}
          </div>
        </section>

        {/* 退出登录 */}
        <button
          className={styles.logoutBtn}
          onClick={handleLogout}
          disabled={loading}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
            <polyline points="16 17 21 12 16 7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
          <span>{loading ? "退出中..." : "退出登录"}</span>
        </button>
      </div>
    </div>
  )
}
